import { chain } from 'lodash';
import moment from 'moment';
import React, { memo } from 'react';
import { ProjectGq } from '../../../../../../graphql/projects/models/ProjectGq';
import { ProjectKeywordGq } from '../../../../../../graphql/projects/models/ProjectKeywordGq';
import { QueuedItemStatus } from '../../../../../../graphql/projects/models/QueuedItemStatus';
import { SerpStatusGq } from '../../../../../../graphql/projects/models/SerpStatusGq';
import { SerpTrackingKeywordRankGq } from '../../../../../../graphql/projects/models/SerpTrackingKeywordRankGq';
import { SerpTrackingRequestGq } from '../../../../../../graphql/projects/models/SerpTrackingRequestGq';
import TableCellRankingUrl from './TableCellRankingUrl';
import TableCellRankTrend from './TableCellRankTrend';
import TableCellRank from './TableCellRank';
import TableCellTrackingRank from './TableCellTrackingRank';
import TableCellTraffic from './TableCellTraffic';
import TableCellTrafficTrend from './TableCellTrafficTrend';
import { ProjectSerpTrackingRequestGq } from '../../../../../../graphql/projects/models/ProjectSerpTrackingRequestGq';
import { CurrentUserGq } from '../../../../../../graphql/users/models/CurrentUserGq';

export interface TableCellDomainProps {
  project: ProjectGq;
  keyword: ProjectKeywordGq;
  domainId: number;
  showRankColor: boolean;
  showUrl: boolean;
  showTraffic: boolean;
  rankCtrValues?: Record<string, number>;
  isProgressPage?: boolean;
  trackingRequests?: ProjectSerpTrackingRequestGq[];
  serpStatus?: SerpStatusGq;
  user?: CurrentUserGq;
}

export interface TrackingData {
  scheduledDate: moment.Moment;
  requestDate?: moment.Moment;
  trackingRequest?: SerpTrackingRequestGq;
  trackingRank?: SerpTrackingKeywordRankGq;
}

// The entire cell is memoized (because there's a lot of little loops and calculations throughout, we don't have to use memo within the components)
const TableCellDomain: React.FC<TableCellDomainProps> = memo(
  ({
    project,
    keyword,
    domainId,
    showRankColor,
    showUrl,
    showTraffic,
    rankCtrValues,
    isProgressPage,
    trackingRequests,
    serpStatus,
    user,
  }) => {
    //Initial rank value (first fetch is not included among tracking values)
    const keywordDomainRank = keyword.domainRanks?.find(
      (dr) => !!dr?.rank && dr?.domainId === domainId
    );
    const domain = project.domains.find((d) => d.id === domainId);

    // Tracking values
    const allTrackingRequests = chain(trackingRequests)
      .map((srt): TrackingData => {
        const request = keyword.serpTrackingRequests?.find(
          (trackingRequest) => trackingRequest?.projectRequestId === srt.id
        );
        const rank = keyword.serpTrackingKeywordRanks?.find(
          (trackingRank) =>
            trackingRank.domainId === domainId && trackingRank.projectRequestId == srt.id
        );
        return {
          scheduledDate: moment(srt.scheduledTime),
          requestDate: request ? moment(request.serpTrackingTaskTime) : undefined,
          trackingRank: rank,
          trackingRequest: request,
        };
      })
      .value();

    const completedTrackingRequests = chain(allTrackingRequests)
      .filter((tv) => tv.trackingRequest?.serpStatus === SerpStatusGq.COMPLETED)
      .orderBy((v) => v.scheduledDate)
      .value();

    const firstTrackingValue = completedTrackingRequests[0];
    const lastTrackingValue = completedTrackingRequests.reverse()[0];

    return (
      <>
        {/* Rank */}
        {isProgressPage && (
          <TableCellTrackingRank
            trackingValues={allTrackingRequests}
            firstTrackingValue={firstTrackingValue}
            lastTrackingValue={lastTrackingValue}
            rankColor={showRankColor}
            keyword={keyword}
            domain={domain}
            borderLeft
          />
        )}

        {!isProgressPage && (
          <TableCellRank
            rankColor={showRankColor}
            keyword={keyword}
            domainId={domainId}
            project={project}
            keywordDomainRank={keywordDomainRank}
            borderLeft={showUrl || showTraffic}
            serpStatus={serpStatus}
            user={user}
          />
        )}

        {/* Rank trend +/- */}
        {isProgressPage && (
          <TableCellRankTrend
            firstTrackingValue={firstTrackingValue}
            lastTrackingValue={lastTrackingValue}
            borderRight={!(showTraffic || showUrl)}
          />
        )}

        {/* Traffic */}
        {!isProgressPage && showTraffic && (
          <TableCellTraffic
            keyword={keyword}
            rankCtrValues={rankCtrValues}
            rank={keywordDomainRank?.rank}
            dataExists={!!keyword.serpResultTime && !keyword.fetchingNewVolume}
            borderRight={!showUrl}
            serpStatus={serpStatus}
          />
        )}
        {isProgressPage && showTraffic && (
          <TableCellTraffic
            keyword={keyword}
            rankCtrValues={rankCtrValues}
            rank={lastTrackingValue?.trackingRank?.rank}
            dataExists={lastTrackingValue?.trackingRequest?.serpStatus === SerpStatusGq.COMPLETED}
          />
        )}

        {/* Traffic trend +/- */}
        {showTraffic && isProgressPage && (
          <TableCellTrafficTrend
            firstTrackingValue={firstTrackingValue}
            lastTrackingValue={lastTrackingValue}
            keyword={keyword}
            rankCtrValues={rankCtrValues}
            borderRight={!showUrl}
          />
        )}

        {/* Ranking url */}
        {!isProgressPage && showUrl && (
          <TableCellRankingUrl
            url={keywordDomainRank?.url}
            domain={domain?.hostName}
            borderRight
            serpStatus={serpStatus}
            keyword={keyword}
          />
        )}
        {isProgressPage && showUrl && (
          <TableCellRankingUrl
            url={lastTrackingValue?.trackingRank?.url}
            domain={domain.hostName}
            borderRight
            keyword={keyword}
          />
        )}
      </>
    );
  }
);

export default TableCellDomain;
